import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map, tap } from 'rxjs';
import { ConfigService } from './config.service';
import {
  ApiDevisGroups,
  ApiDevisProject,
  ApiPolicy,
  ApiPolicyAction,
  ApiPolicyControl,
  ApiPolicyDetail,
  ApiPolicyRisk,
  ApiPolicyStep,
  ApiPolicySuperAction,
  ApiRemoteImage,
  ApiSinister,
  ApiSinisterGroups,
} from '../models/myspace-api.models';
import { ApiConfigService } from './api-config.service';

@Injectable({ providedIn: 'root' })
export class MyspaceApiService {
  constructor(
    private readonly http: HttpClient,
    private readonly apiConfig: ApiConfigService,
    private readonly configService: ConfigService,
  ) {}

  /** GET /api-produit{suffixe}/policy/v2/find?phoneNumber= */
  listPolicies(phoneNumber: string): Observable<ApiPolicy[]> {
    const url = this.apiConfig.policyListUrl;
    return this.http.get<unknown>(url, { params: { phoneNumber } }).pipe(
      map((raw) => this.parsePolicies(raw)),
      tap((response) => {
        if (this.configService.enableApiLogs) {
          console.log('[MyspaceApi] policies', url, phoneNumber, response);
        }
      }),
    );
  }

  /** GET /api-produit{suffixe}/project/v2/contractless/find?phoneNumber= */
  listDevis(phoneNumber: string): Observable<ApiDevisGroups> {
    const url = this.apiConfig.contractlessFindUrl;
    return this.http.get<unknown>(url, { params: { phoneNumber } }).pipe(
      map((raw) => this.parseGrouped<ApiDevisProject>(raw)),
      tap((response) => {
        if (this.configService.enableApiLogs) {
          console.log('[MyspaceApi] devis', url, phoneNumber, response);
        }
      }),
    );
  }

  /** DELETE /api-souscription{suffixe}/project/v1/delete */
  deleteDevis(phoneNumber: string, id: string): Observable<unknown> {
    const url = this.apiConfig.contractlessDeleteUrl;
    return this.http
      .delete(url, {
        body: { phoneNumber, id },
        headers: { 'Content-Type': 'application/json' },
      })
      .pipe(
        tap((response) => {
          if (this.configService.enableApiLogs) {
            console.log('[MyspaceApi] delete devis', url, response);
          }
        }),
      );
  }

  /** POST /api-produit{suffixe}/policy/v1/find-policy-detail */
  getPolicyDetail(
    dto: Record<string, string | number>,
  ): Observable<ApiPolicyDetail> {
    const url = this.apiConfig.policyDetailUrl;
    return this.http.post<ApiPolicyDetail>(url, dto).pipe(
      map((raw) => this.slimPolicyDetail(raw)),
      tap((response) => {
        if (this.configService.enableApiLogs) {
          console.log('[MyspaceApi] policy detail', url, dto, {
            sections: response.policyItemsSections?.length ?? 0,
            superActions: response.superActions?.length ?? 0,
          });
        }
      }),
    );
  }

  /** GET /api-design{suffixe}/sinistre/v1/print/{userId}/{policy}/{reference} */
  downloadSinisterReceipt(
    userId: string,
    policy: string,
    reference: string,
  ): Observable<unknown> {
    const url = this.apiConfig.buildSinisterPrintUrl(userId, policy, reference);
    return this.http.get<unknown>(url, { params: { download: 'false' } }).pipe(
      tap((response) => {
        if (this.configService.enableApiLogs) {
          console.log('[MyspaceApi] sinister print', url, response);
        }
      }),
    );
  }

  /** GET /api-design{suffixe}/sinistre/findby/user?userId= */
  listSinisters(userId: string): Observable<ApiSinisterGroups> {
    const url = this.apiConfig.sinisterByUserUrl;
    return this.http
      .get<unknown>(url, { params: { userId }, observe: 'response' })
      .pipe(
        map((response) => {
          if (response.status === 204 || response.body == null) {
            return {};
          }
          return this.parseGrouped<ApiSinister>(response.body);
        }),
        tap((response) => {
          if (this.configService.enableApiLogs) {
            console.log('[MyspaceApi] sinisters', url, response);
          }
        }),
      );
  }

  /** POST /api-design{suffixe}/sinistre/create (multipart, aligné mobile). */
  createSinister(
    fields: Record<string, string>,
    files: File[] = [],
  ): Observable<Record<string, unknown>> {
    const url = this.apiConfig.sinisterCreateUrl;
    const body = new FormData();
    Object.entries(fields).forEach(([key, value]) => {
      if (value != null) {
        body.append(key, value);
      }
    });
    files.forEach((file) => body.append('files', file, file.name));
    return this.http.post<Record<string, unknown>>(url, body).pipe(
      tap((response) => {
        if (this.configService.enableApiLogs) {
          console.log('[MyspaceApi] create sinister', url, response);
        }
      }),
    );
  }

  private slimPolicyDetail(
    raw: ApiPolicyDetail | null | undefined,
  ): ApiPolicyDetail {
    const quittance = raw?.quittanceDetail;
    const primtota = quittance?.primtota ?? quittance?.PRIMTOTA ?? null;
    const libeperi = quittance?.libeperi ?? quittance?.LIBEPERI ?? null;
    const dateeffe = quittance?.dateeffe ?? quittance?.DATEEFFE ?? null;
    const dateeche = quittance?.dateeche ?? quittance?.DATEECHE ?? null;
    const sinisterDetail = raw?.sinisterDetail;
    return {
      quittanceDetail: quittance
        ? {
            primtota,
            PRIMTOTA: primtota,
            libeperi,
            LIBEPERI: libeperi,
            dateeffe,
            DATEEFFE: dateeffe,
            dateeche,
            DATEECHE: dateeche,
          }
        : null,
      policyItemsSections: (raw?.policyItemsSections || []).map((section) => ({
        title: section.title ?? '',
        type: section.type ?? '',
        items: (section.items || []).map((item) => ({
          label: item.label ?? '',
          value: item.value ?? '',
          name: item.name ?? '',
          type: item.type ?? '',
          lienParente: item.lienParente ?? '',
          partBeneficiaire: item.partBeneficiaire ?? '',
          natugara: item.natugara ?? '',
        })),
      })),
      superActions: this.slimSuperActions(raw?.superActions),
      riskbs: this.slimRisks(raw?.riskbs),
      sinisterType: raw?.sinisterType ?? '',
      sinisterDetail: sinisterDetail
        ? {
            secondDetailTitle: String(sinisterDetail.secondDetailTitle ?? ''),
            successMessage: String(sinisterDetail.successMessage ?? ''),
            claimNammings: String(sinisterDetail.claimNammings ?? ''),
            dataListDictionnary: this.slimDataListDictionary(
              sinisterDetail.dataListDictionnary,
            ),
          }
        : null,
    };
  }

  private slimSuperActions(
    raw: ApiPolicySuperAction[] | null | undefined,
  ): ApiPolicySuperAction[] {
    if (!Array.isArray(raw)) {
      return [];
    }
    return raw
      .map((item) => ({
        id: String(item?.id ?? ''),
        key: String(item?.key ?? ''),
        ordre: Number(item?.ordre ?? 0),
        name: String(item?.name ?? ''),
        label: String(item?.label ?? ''),
        image: this.slimImage(item?.image),
        actions: this.slimActions(item?.actions),
      }))
      .sort((a, b) => (a.ordre ?? 0) - (b.ordre ?? 0));
  }

  private slimActions(
    raw: ApiPolicyAction[] | null | undefined,
  ): ApiPolicyAction[] {
    if (!Array.isArray(raw)) {
      return [];
    }
    return raw
      .map((item) => ({
        id: String(item?.id ?? ''),
        key: String(item?.key ?? ''),
        ordre: Number(item?.ordre ?? 0),
        name: String(item?.name ?? ''),
        label: String(item?.label ?? ''),
        image: this.slimImage(item?.image),
        screenShotsNumber: Number(item?.screenShotsNumber ?? 0),
        screenShotMinRequired: Number(item?.screenShotMinRequired ?? 0),
        screenShotValidMsg: String(item?.screenShotValidMsg ?? ''),
        steps: this.slimSteps(item?.steps),
      }))
      .sort((a, b) => (a.ordre ?? 0) - (b.ordre ?? 0));
  }

  private slimSteps(raw: ApiPolicyStep[] | null | undefined): ApiPolicyStep[] {
    if (!Array.isArray(raw)) {
      return [];
    }
    return raw
      .map((item) => ({
        id: String(item?.id ?? ''),
        name: String(item?.name ?? ''),
        label: String(item?.label ?? ''),
        ordre: Number(item?.ordre ?? 0),
        type: String(item?.type ?? ''),
        controls: this.slimControls(item?.controls),
      }))
      .sort((a, b) => (a.ordre ?? 0) - (b.ordre ?? 0));
  }

  private slimControls(
    raw: ApiPolicyControl[] | null | undefined,
  ): ApiPolicyControl[] {
    if (!Array.isArray(raw)) {
      return [];
    }
    return raw
      .map((item) => ({
        id: String(item?.id ?? ''),
        name: String(item?.name ?? ''),
        label: String(item?.label ?? ''),
        validationMessage: String(item?.validationMessage ?? ''),
        type: String(item?.type ?? 'text'),
        maxLine: Number(item?.maxLine ?? 1),
        ordre: Number(item?.ordre ?? 0),
        required: Boolean(item?.required),
        showToSuccess: Boolean(item?.showToSuccess),
        contraintTypeName: String(item?.contraintTypeName ?? ''),
        requiredDataListType: item?.requiredDataListType
          ? String(item.requiredDataListType)
          : null,
        readOnly: Boolean(item?.readOnly),
        initialValue: item?.initialValue ?? '',
        enabled: item?.enabled !== false,
        minimum: item?.minimum == null ? null : Number(item.minimum),
        maximum: item?.maximum == null ? null : Number(item.maximum),
        format: item?.format ? String(item.format) : null,
      }))
      .sort((a, b) => (a.ordre ?? 0) - (b.ordre ?? 0));
  }

  private slimImage(raw: ApiRemoteImage | null | undefined): ApiRemoteImage {
    if (!raw || typeof raw !== 'object') {
      return { id: '', url: '' };
    }
    return {
      id: String(raw.id ?? ''),
      url: String(raw.url ?? ''),
    };
  }

  private slimDataListDictionary(
    raw: Record<string, unknown> | null | undefined,
  ): Record<string, Array<Record<string, unknown>>> {
    if (!raw || typeof raw !== 'object') {
      return {};
    }
    return Object.entries(raw).reduce<
      Record<string, Array<Record<string, unknown>>>
    >((acc, [key, value]) => {
      acc[key] = Array.isArray(value)
        ? value.map((item) =>
            item && typeof item === 'object'
              ? (item as Record<string, unknown>)
              : { label: String(item), code: String(item) },
          )
        : [];
      return acc;
    }, {});
  }

  private slimRisks(raw: ApiPolicyRisk[] | null | undefined): ApiPolicyRisk[] {
    if (!Array.isArray(raw)) {
      return [];
    }
    return raw.map((item) => ({
      ...item,
      CODEINTE: item?.CODEINTE ?? null,
      NUMEPOLI: item?.NUMEPOLI ?? null,
      NOM_MEMB: item?.NOM_MEMB ?? item?.nomMemb ?? null,
      nomMemb: item?.nomMemb ?? item?.NOM_MEMB ?? null,
      LIBLIEPA: item?.LIBLIEPA ?? null,
      LIENPARE: item?.LIENPARE ?? null,
    }));
  }

  private parsePolicies(raw: unknown): ApiPolicy[] {
    if (raw == null) {
      return [];
    }
    if (Array.isArray(raw)) {
      return raw as ApiPolicy[];
    }
    if (typeof raw !== 'object') {
      return [];
    }

    const record = raw as Record<string, unknown>;
    if (Array.isArray(record['data'])) {
      return record['data'] as ApiPolicy[];
    }
    if (Array.isArray(record['content'])) {
      return record['content'] as ApiPolicy[];
    }

    const grouped: ApiPolicy[] = [];
    Object.values(record).forEach((value) => {
      if (Array.isArray(value)) {
        grouped.push(...(value as ApiPolicy[]));
      }
    });
    return grouped;
  }

  private parseGrouped<T>(raw: unknown): Record<string, T[]> {
    if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) {
      if (Array.isArray(raw)) {
        return { '': raw as T[] };
      }
      return {};
    }

    const record = raw as Record<string, unknown>;
    const result: Record<string, T[]> = {};
    Object.entries(record).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        result[key] = value as T[];
      }
    });
    return result;
  }
}
